import { Trans } from 'react-i18next';
import { type TFunction } from 'i18next';
import { colors, Heading, Link, Paragraph } from 'component-library';
import { BodyBox, RichTextList, RichTextListItem } from './styles';

type RichTextBodyProps = {
  body: string;
  whitelist?: string[];
  blackLinks?: boolean;
};

//We use Trans-component to parse the html and replace the tags with the components we want to use but we do not use it for translation so we need to provide a fake TFunction to avoid errors
const fakeT = ((key: string) => key) as TFunction;

export const RichTextBody = ({ body, whitelist, blackLinks }: RichTextBodyProps) => {
  const anchorTagRegex = /<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>(.*?)<\/a>/gi;
  const style = / style.*?;"/g;

  // Object map to store extracted values
  const linkMap: Record<string, string> = {};

  // Counter for link identifiers
  let linkCounter = 1;

  // Replace anchor tags and populate the object map
  const replacedHtml = body.replace(anchorTagRegex, (match, href, linkText) => {
    const linkKey = `link${linkCounter++}`;

    linkMap[linkKey] = href as string;

    return `<${linkKey}>${linkText}</${linkKey}>`;
  });

  //Remove style tags
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  let _replacedHtml = unescape(replacedHtml.replace(style, '').replace(/class=/g, 'className='));

  // Remove any tags that are not in the whitelist
  if (whitelist) {
    const linkKeys = Object.keys(linkMap);

    _replacedHtml = _replacedHtml.replace(
      /<\/?(\w+)[^>]*>(.+)<\/\1>/g,
      (match, tagName: string, content: string) => {
        if (whitelist.includes(tagName) || linkKeys.includes(tagName)) {
          return match; // Keep the tag as is if it's in the whitelist
        }

        return content; // Replace with an empty tag if not in the whitelist
      },
    );
  }

  const components: Record<string, React.JSX.Element> = {
    h1: <Heading as="h1" mb="small" variant="large"></Heading>,
    h2: <Heading as="h2" mb="small" variant="large"></Heading>,
    h3: <Heading as="h3" mb="small" variant="large"></Heading>,
    h4: <Heading as="h4" mb="small" variant="large"></Heading>,
    h5: <Heading as="h5" mb="small" variant="large"></Heading>,
    em: <em></em>,
    strong: <strong></strong>,
    p: <Paragraph mb="small" variant="large"></Paragraph>,
    ul: <RichTextList />,
    li: <RichTextListItem />,
    div: <BodyBox></BodyBox>,
    span: <span></span>,
    sub: <sub></sub>,
    sup: <sup></sup>,
    br: <br />,
    s: <s></s>,
  };

  // Add links to the components object
  Object.keys(linkMap).forEach((key) => {
    components[key] = (
      <Link
        inline={true}
        href={linkMap[key]}
        color={blackLinks ? colors.base.black : undefined}
        underline={!!blackLinks}
      >
        {linkMap[key]}
      </Link>
    );
  });

  return (
    <Trans
      components={components}
      tOptions={{ interpolation: { escapeValue: true } }}
      shouldUnescape
      t={fakeT}
    >
      {htmlDecode(_replacedHtml)}
    </Trans>
  );
};

function htmlDecode(input: string) {
  const e = document.createElement('textarea');

  e.innerHTML = input;

  // handle case of empty input
  return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
}
